import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormControl,
} from '@angular/forms';
import { Router } from '@angular/router';
import { NotifierService } from 'angular-notifier';
import { BoobleModalService } from 'booble-modal';
import { OrdersService } from 'src/app/shared/services/orders.service';
import { CorporateAccountService } from 'src/app/shared/services/corporate-account.service';
import { MatSelect } from '@angular/material/select';
import { ReplaySubject, Subject, firstValueFrom, of } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { BusinessPartnerDto } from 'src/app/domain/models/dto/business-partner.dto';
import { ResponseObjectModel } from 'src/app/domain/models/response-object.model';
import { EPersonType } from 'src/app/domain/enums/person-type.enum';
import { ClientConfigComponent } from '../client-config/client-config.component';
import { Clipboard } from '@angular/cdk/clipboard';
import { HistoryDto } from 'src/app/domain/models/dto/history.dto';
import { ResponseTableModel } from 'src/app/domain/models/response-table.model';
import {
  getStatusClass,
  getStatusLabel,
  getSituationIcon,
  getSituationLabel,
  getSituationClass,
} from 'src/app/shared/utils/client-status.util';

@Component({
  selector: 'app-client-details',
  templateUrl: './client-details.component.html',
  styleUrls: ['./client-details.component.scss'],
})
export class ClientDetailsComponent implements OnInit, OnDestroy {
  private readonly notifier: NotifierService;
  boobleModalData: any;
  selectedTabIndex: number = 0;
  public loading = false;
  public haveLog = false;
  configForm!: FormGroup;

  logDisplayedColumns: string[] = ['user', 'date'];
  logData: ResponseTableModel<HistoryDto[]>;
  logPageNumber: number = 0;
  logPageSize: number = 10;
  logTotalElements: number = 0;
  isLoadingLog: boolean = false;

  partners: BusinessPartnerDto[] = [];
  agencies: BusinessPartnerDto[] = [];
  agencyFilterCtrl = new FormControl('');
  filteredAgencies: ReplaySubject<BusinessPartnerDto[]> = new ReplaySubject(1);

  @ViewChild('agencySelect', { static: false }) agencySelect!: MatSelect;
  private _onDestroy = new Subject<void>();

  get config(): any {
    return this.configForm.value;
  }

  constructor(
    private router: Router,
    private modalService: BoobleModalService,
    notifierService: NotifierService,
    private ordersService: OrdersService,
    private formBuilder: FormBuilder,
    private companyService: CorporateAccountService,
    private clipboard: Clipboard
  ) {
    this.notifier = notifierService;

    this.configForm = this.formBuilder.group({
      id: [null],
      isDefaultTariff: [null, Validators.required],
      isDefaultSpread: [null, Validators.required],
      businessPartnerId: ['', Validators.required],
      profileType: ['', Validators.required],
      personType: [null, Validators.required],
      spread: [''],
      tariff: [''],
    });
  }

  async ngOnInit() {
    const partnersRes = await firstValueFrom(
      this.companyService
        .getPartnersList()
        .pipe(
          catchError(() =>
            of({ data: [] } as ResponseObjectModel<BusinessPartnerDto[]>)
          )
        )
    );

    this.partners = partnersRes.data ?? [];
    this.agencies = this.partners;
    this.filteredAgencies.next(this.agencies);

    this.agencyFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => this.filterAgencies());

    await this.loadClientDetails();
    this.loadLogData();
  }

  async loadClientDetails() {
    const initialData = this.boobleModalData ?? {};
    const clientId = initialData?.clientId;
    const personType = this.extractPersonType(initialData?.personType);

    if (!clientId || !personType) {
      this.notifier.notify(
        'error',
        'Dados insuficientes para carregar detalhes do cliente'
      );
      return;
    }

    this.loading = true;

    try {
      const result = await firstValueFrom(
        this.companyService.getAccountStatusDetail(clientId, personType)
      );

      if (result.success && result.data) {
        const data = result.data;
        this.boobleModalData = data;

        this.configForm.patchValue({
          id: data.clientId,
          isDefaultTariff: data.isDefaultTariff ?? null,
          isDefaultSpread: data.isDefaultSpread ?? null,
          businessPartnerId: data.businessPartner?.id ?? '',
          profileType: data.profileType ?? '',
          personType: this.mapPersonTypeToEnum(data.personType),
        });

        this.configForm.patchValue({
          tariff: data.tariff ?? '',
          spread: data.spread ?? '',
        });

        if (data.isDefaultTariff === false) {
          this.configForm
            .get('tariff')
            ?.setValidators([
              Validators.required,
              Validators.pattern('.*\\S.*'),
            ]);
          this.configForm.get('tariff')?.enable();
        } else {
          this.configForm.get('tariff')?.disable();
        }

        if (data.isDefaultSpread === false) {
          this.configForm
            .get('spread')
            ?.setValidators([
              Validators.required,
              Validators.pattern('.*\\S.*'),
            ]);
          this.configForm.get('spread')?.enable();
        } else {
          this.configForm.get('spread')?.disable();
        }
      } else {
        this.notifier.notify('error', 'Erro ao carregar detalhes do cliente');
      }
    } catch (error) {
      this.notifier.notify('error', 'Erro ao carregar detalhes do cliente');
    } finally {
      this.loading = false;
    }
  }

  private extractPersonType(personType: string): string {
    if (!personType) return '';

    if (personType.includes('Jurídica') || personType === 'PJ') return 'Pj';
    if (personType.includes('Física') || personType === 'PF') return 'Pf';

    return personType;
  }

  closeModal() {
    this.modalService.dismiss(null);
  }

  openClientConfigModal(client: any) {
    this.modalService.present(ClientConfigComponent, client);
  }

  copyText(text: string) {
    if (!text) {
      this.notifier.notify('warning', 'Não há texto para copiar');
      return;
    }
    this.clipboard.copy(text);
    this.notifier.notify('success', 'Texto copiado para área de transferência');
  }

  getLimitPercentage(): number {
    const total = this.boobleModalData?.opLimit || 0;
    const used = this.getUsedLimit();

    if (total === 0) return 0;

    return (used / total) * 100;
  }

  getRemainingPercentage(): number {
    const total = this.boobleModalData?.opLimit || 0;
    const remaining = this.boobleModalData?.remainingOpLimit || 0;

    if (total === 0) return 0;

    return (remaining / total) * 100;
  }

  getUsedLimit(): number {
    const total = this.boobleModalData?.opLimit || 0;
    const remaining = this.boobleModalData?.remainingOpLimit || 0;
    return total - remaining;
  }

  getStatusLabel = getStatusLabel;
  getStatusClass = getStatusClass;

  loadLogData() {
    const clientId = this.boobleModalData?.clientId;
    const personType =
      this.boobleModalData?.personType == 'PJ - Pessoa Jurídica'
        ? 'CorporateAccount'
        : 'IndividualAccount';

    this.isLoadingLog = true;
    this.companyService
      .getCorporateAccountHistory(
        clientId,
        personType,
        this.logPageNumber + 1,
        this.logPageSize
      )
      .subscribe({
        next: (result) => {
          if (result.success && result.data?.content.length > 0) {
            this.haveLog = true;
            this.logData = result.data;
            this.logTotalElements = result.data.totalElements || 0;
          } else {
            this.haveLog = false;
            this.logData = null;
            this.logTotalElements = 0;
          }
          this.isLoadingLog = false;
        },
        error: (error) => {
          this.haveLog = false;
          this.logData = null;
          this.logTotalElements = 0;
          this.isLoadingLog = false;
        },
      });
  }

  onLogPageSizeChange() {
    this.logPageNumber = 0;
    this.loadLogData();
  }

  onLogPreviousPage() {
    if (this.logPageNumber > 0) {
      this.logPageNumber--;
      this.loadLogData();
    }
  }

  onLogNextPage() {
    if ((this.logPageNumber + 1) * this.logPageSize < this.logTotalElements) {
      this.logPageNumber++;
      this.loadLogData();
    }
  }

  updateLogPageData(event: { pageNumber: number; pageSize: number }) {
    this.logPageNumber = event.pageNumber;
    this.logPageSize = event.pageSize;
    this.loadLogData();
  }

  getFullAddress(
    address?: string,
    number?: string,
    postalCode?: string
  ): string {
    const addr = address || '';
    const num = number || '';
    const postal = postalCode || '';

    let fullAddress = '';

    if (addr) {
      fullAddress += addr;
    }

    if (num) {
      fullAddress += fullAddress ? `, ${num}` : num;
    }

    if (postal) {
      const formattedPostalCode = postal.replace(/(\d{5})(\d{3})/, '$1-$2');
      fullAddress += fullAddress
        ? `, ${formattedPostalCode}`
        : formattedPostalCode;
    }

    return fullAddress || 'N/A';
  }

  getPartnerFullAddress(addressObj?: {
    line1?: string;
    line3?: string;
    number?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  }): string {
    if (!addressObj) {
      return 'N/A';
    }

    const parts: string[] = [];

    if (addressObj.line1) {
      parts.push(addressObj.line1);
    }

    if (addressObj.number) {
      parts.push(addressObj.number);
    }

    if (addressObj.line3) {
      parts.push(addressObj.line3);
    }

    if (addressObj.city) {
      parts.push(addressObj.city);
    }

    if (addressObj.state) {
      parts.push(addressObj.state);
    }

    if (addressObj.postalCode) {
      const formattedPostalCode = addressObj.postalCode.replace(
        /(\d{5})(\d{3})/,
        '$1-$2'
      );
      parts.push(formattedPostalCode);
    }

    return parts.length > 0 ? parts.join(', ') : 'N/A';
  }

  private filterAgencies() {
    const term = (this.agencyFilterCtrl.value || '').toString().toLowerCase();
    if (!term) {
      this.filteredAgencies.next(this.agencies);
      return;
    }
    this.filteredAgencies.next(
      this.agencies.filter((a) => {
        const name = (a.companyName || '').toLowerCase();
        const agency = String(a.agencyNumber ?? '');
        return name.includes(term) || agency.includes(term);
      })
    );
  }

  private mapPersonTypeToEnum(raw: unknown): EPersonType | null {
    if (raw === 'PJ - Pessoa Jurídica' || raw === 'PJ') return EPersonType.PJ;
    if (raw === 'PF - Pessoa Física' || raw === 'PF') return EPersonType.PF;

    if (raw === EPersonType.PF || raw === EPersonType.PJ)
      return raw as EPersonType;
    return null;
  }

  onSpreadChange(event: any) {
    const isDefaultSpreadValue = event.value === true;
    const spreadControl = this.configForm.get('spread');

    if (isDefaultSpreadValue) {
      spreadControl?.clearValidators();
      spreadControl?.setValue(this.boobleModalData?.spread ?? '');
      spreadControl?.disable();
    } else {
      spreadControl?.setValidators([
        Validators.required,
        Validators.pattern('.*\\S.*'),
      ]);
      spreadControl?.enable();
    }
    spreadControl?.updateValueAndValidity();
  }

  onTariffChange(event: any) {
    const isDefaultTariffValue = event.value === true;
    const tariffControl = this.configForm.get('tariff');

    if (isDefaultTariffValue) {
      tariffControl?.clearValidators();
      tariffControl?.setValue(this.boobleModalData?.tariff ?? '');
      tariffControl?.disable();
    } else {
      tariffControl?.setValidators([
        Validators.required,
        Validators.pattern('.*\\S.*'),
      ]);
      tariffControl?.enable();
    }
    tariffControl?.updateValueAndValidity();
  }

  async sendConfig() {
    if (this.configForm.invalid) {
      this.configForm.markAllAsTouched();
      return;
    }

    this.loading = true;

    const payload = { ...this.config };

    this.companyService.setClientConfig(payload).subscribe({
      next: async () => {
        this.notifier.notify('success', 'Configuração salva com sucesso!');
        await this.loadClientDetails();
        this.loadLogData();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  getSituationIcon = getSituationIcon;
  getSituationLabel = getSituationLabel;
  getSituationClass = getSituationClass;

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }
}
